import type {
  BlocksOutput,
  BranchChangesets,
  LayersOutput,
} from '@festina/configuration-general-types/configuration'
import datamartGeneralOpenApiServices from '@festina/datamart-general-types'
import type { OpenApiType } from '@festina-life/authentication'
import { notEmpty, type OpenAPIConfig } from '@festina-life/core'

import { DEFAULT_BLOCK } from '../system/constants'
import { convertConfigurationPackageToBranchChangesets } from './helpers'
import {
  CollCalcService,
  CollCalcTestService,
  ConfigurationService,
  ConfigurationTestService,
  LegalEntityService,
  LegalEntityTestService,
  LifeService,
  LifeTestService,
  MoneyInService,
  MoneyInTestService,
  MoneyOutService,
  MoneyOutTestService,
  UnitLinkService,
  UnitLinkTestService,
} from './openAPIServicesImportMap'

type ServerService =
  | typeof CollCalcService
  | typeof LegalEntityService
  | typeof LifeService
  | typeof MoneyInService
  | typeof MoneyOutService
  | typeof UnitLinkService

const wrapAPI = <T extends ServerService>(ServicesCollection: T) => ({
  ...ServicesCollection,
  ConfigurationMetaService: {
    ...ServicesCollection.ConfigurationMetaService,
    metaServiceGetGetBranchModel: ({
      componentName,
      ...params
    }: Parameters<
      typeof ConfigurationService.ConfigurationMetaService.metaServiceGetGetBranchModel
    >[0]) =>
      ServicesCollection.ConfigurationMetaService.metaServiceGetModel(params),
    metaServiceGetGetBranchLayerModel: ({
      componentName,
      layer,
      ...params
    }: Parameters<
      typeof ConfigurationService.ConfigurationMetaService.metaServiceGetGetBranchLayerModel
    >[0]) =>
      !layer
        ? ServicesCollection.ConfigurationMetaService.metaServiceGetModel(
            params,
          )
        : null,
    metaServiceGetGetLayers: () =>
      Promise.resolve({
        _type: 'LayersOutput',
        layers: [],
        isStrictlyMoreGeneralPairs: [],
      } as const satisfies LayersOutput),
    metaServiceGetGetBlocks: () =>
      Promise.resolve({
        _type: 'BlocksOutput',
        blocks: [DEFAULT_BLOCK],
      } as const satisfies BlocksOutput),
  },
  ConfigurationRepositoryBranchesService: {
    configurationServiceGetGetBulkChangesets: (
      // The configuration server branchServiceGetGetBranchChangesets is actually a bulk export so we reuse its params
      params: Parameters<
        typeof ConfigurationService.ConfigurationRepositoryBranchesService.branchServiceGetGetBranchChangesets
      >[0] & { shouldIncludeExtrinsicParameterValues: boolean },
    ) =>
      ServicesCollection.ConfigurationChangesetsService.configurationServiceGetGetBulkChangesets(
        {
          includeExtrinsicParameters:
            params.shouldIncludeExtrinsicParameterValues ?? undefined,
        },
      ),
    branchServiceGetGetBranchBlockChangesets: async (
      params: Parameters<
        typeof ConfigurationService.ConfigurationRepositoryBranchesService.branchServiceGetGetBranchBlockChangesets
      >[0] & {
        shouldIncludeExtrinsicParameterValues: boolean
      },
    ) => {
      const standardComponentTenants =
        await ServicesCollection.ConfigurationTenantsService.tenantsServiceGetGetRegisteredTenants(
          {},
        )
      const allTenantIds =
        standardComponentTenants?.registeredTenants
          ?.map((tenant) => tenant?.tenantId)
          .filter(notEmpty) ?? []

      // We fetch all tenant changesets or the null tenant changeset if no tenants are registered
      const fetchAll = (allTenantIds.length ? allTenantIds : [null]).map(
        (tenantId) =>
          ServicesCollection.ConfigurationChangesetsService.configurationServiceGetGetChangesets(
            {
              xTenantId: tenantId ?? undefined,
              includeExtrinsicParameters:
                params.shouldIncludeExtrinsicParameterValues,
            },
          ),
      )

      const allResults = await Promise.all(fetchAll)

      // Aggregate all tenant results into a single ConfigurationPackage
      return allResults.length
        ? allResults.reduce<BranchChangesets>(
            (acc, result) => {
              const branchChangesetsForPackage = result
                ? convertConfigurationPackageToBranchChangesets(
                    result,
                    DEFAULT_BLOCK, // We never use the block filtering capabilities of the backend. For the connected component we simply assign everything to the prod block
                  )
                : null

              return {
                ...acc,
                branchChangesets: [
                  ...(acc.branchChangesets ?? []),
                  ...(branchChangesetsForPackage?.branchChangesets ?? []),
                ],
              } satisfies BranchChangesets
            },
            {
              _type: 'BranchChangesets',
              branchChangesets: [],
            },
          )
        : null
    },
  },
  ConfigurationRepositoryWorkspacesService: {
    workspaceServiceGetGetWorkspaceBaseBranchChangesets: async () => {
      const result =
        await ServicesCollection.ConfigurationChangesetsService.configurationServiceGetGetBulkChangesets(
          {},
        )

      return convertConfigurationPackageToBranchChangesets(result)
    },
  },
})

export const ServerServices = {
  configuration: {
    api: ConfigurationService,
    test: ConfigurationTestService,
  },
  collCalc: {
    api: wrapAPI(CollCalcService),
    test: CollCalcTestService,
  },
  life: {
    api: wrapAPI(LifeService),
    test: LifeTestService,
  },
  unitLink: {
    api: wrapAPI(UnitLinkService),
    test: UnitLinkTestService,
  },
  legalEntity: {
    api: wrapAPI(LegalEntityService),
    test: LegalEntityTestService,
  },
  moneyIn: {
    api: wrapAPI(MoneyInService),
    test: MoneyInTestService,
  },
  moneyOut: {
    api: wrapAPI(MoneyOutService),
    test: MoneyOutTestService,
  },
}

export const OpenApiConfigurations = {
  collcalc: CollCalcService.OpenAPI,
  collcalcTest: CollCalcTestService.OpenAPI,
  configuration: ConfigurationService.OpenAPI,
  configurationTest: ConfigurationTestService.OpenAPI,
  life: LifeService.OpenAPI,
  lifeTest: LifeTestService.OpenAPI,
  unitlink: UnitLinkService.OpenAPI,
  unitlinkTest: UnitLinkTestService.OpenAPI,
  legalentity: LegalEntityService.OpenAPI,
  legalentityTest: LegalEntityTestService.OpenAPI,
  moneyin: MoneyInService.OpenAPI,
  moneyinTest: MoneyInTestService.OpenAPI,
  moneyout: MoneyOutService.OpenAPI,
  moneyoutTest: MoneyOutTestService.OpenAPI,
  //Datamart is not used in the configuration portal. But to change time this is needed here
  ...datamartGeneralOpenApiServices,
} satisfies Record<Exclude<keyof OpenApiType, 'resetTest'>, OpenAPIConfig>
